/* -----------------Third parties--------------- */
import { List, type ListItemProps } from "react-native-paper";

import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import type { ViewStyle } from "react-native";
import type { Style } from "react-native-paper/lib/typescript/components/List/utils";
import type { IconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import Button from "../Button";
/* -----------------Child components--------------- */
import AudioPlaybackTestSwitch from "./AudioPlaybackTestSwitch";
import DarkModeSwitch from "./DarkModeSwitch";
import { ExamContextListItem } from "./ExamContextListItem";
import PaywallSwitch from "./PaywallSwitch";
/* -----------------Props--------------- */
type Props = {
	type?: string;
	icon: string;
	options?: {
		title: string;
		description: string;
	}[];
	iconRight?: string;
	isDarkMode: boolean;
	showAudioPlaybackTestDialog: boolean;
	showPaywall?: boolean;
	accountExtendValidity: string;
	hasMoreThanOneExam?: boolean;
	toggleDarkMode: () => void;
	toggleAudioPlaybackTestDialog: () => void;
	toggleShowPaywall?: () => void;
	handleNavigateToExamsRoute: () => void;
} & ListItemProps;

/* -----------------Component--------------- */
const AccountListSection = (props: Props) => {
	const {
		type,
		title,
		description,
		icon,
		iconRight,
		isDarkMode,
		showAudioPlaybackTestDialog,
		showPaywall,
		accountExtendValidity,
		hasMoreThanOneExam,
		onPress,
		toggleDarkMode,
		toggleAudioPlaybackTestDialog,
		toggleShowPaywall,
		handleNavigateToExamsRoute,
	} = props;

	const theme = useAppTheme();

	const renderLeft = (p: { color: string; style: Style }) => {
		if (icon.includes(".")) {
			return (
				<List.Image
					{...p}
					variant="image"
					source={{ uri: icon }}
					style={{
						...p.style,
						borderRadius: theme.roundness ? 12 : 0,
					}}
				/>
			);
		}
		return (
			<List.Icon
				{...p}
				icon={(props) => <Icon {...props} name={icon as IconType} />}
			/>
		);
	};
	const renderRight = (propsRight: {
		color: string;
		style?: ViewStyle;
	}) => {
		if (type === "toggle") {
			return (
				<DarkModeSwitch
					isDarkMode={isDarkMode}
					toggleDarkMode={toggleDarkMode}
				/>
			);
		}
		if (type === "toggle-audio-playback") {
			return (
				<AudioPlaybackTestSwitch
					showAudioPlaybackTestDialog={showAudioPlaybackTestDialog}
					toggleAudioPlaybackTestDialog={toggleAudioPlaybackTestDialog}
				/>
			);
		}
		if (type === "toggle-paywall" && toggleShowPaywall !== undefined) {
			return (
				<List.Icon
					{...propsRight}
					icon={() => (
						<PaywallSwitch
							showPaywall={showPaywall ?? false}
							toggleShowPaywall={toggleShowPaywall}
						/>
					)}
				/>
			);
		}
		if (type === "extend-subscription") {
			return (
				<List.Icon
					{...propsRight}
					icon={() => {
						return (
							<Button mode="text" onPress={onPress}>
								{accountExtendValidity}
							</Button>
						);
					}}
				/>
			);
		}
		if (iconRight) {
			return (
				<List.Icon
					{...propsRight}
					icon={(props) => <Icon {...props} name={iconRight as IconType} />}
				/>
			);
		}

		return null;
	};

	const RenderListitem = () => (
		<List.Item
			title={title}
			description={description}
			onPress={onPress}
			style={{
				paddingLeft: 0,
				paddingRight: 16,
			}}
			left={renderLeft}
			right={renderRight}
		/>
	);
	switch (type) {
		case "exam-filter":
			return (
				<ExamContextListItem
					title={title as string}
					icon={icon}
					hasMoreThanOneExam={hasMoreThanOneExam}
					handleNavigateToExamsRoute={handleNavigateToExamsRoute}
				/>
			);
		default:
			return <RenderListitem />;
	}
};

export default AccountListSection;
